import { lowerDecodeLimit, type ProjectDecodeLimits } from './ProjectDecodeLimits';
import { LogicEdgeDtoSchema, type LogicEdgeDto } from '@/schemas/logic';
import type { ProjectSnapshotData } from './ProjectSnapshot';
import {
  SnapshotBlock,
  SnapshotBlockBuilder,
  SnapshotLimit,
  type SnapshotBudgets,
} from './SnapshotBlocks';

// These records are disk metadata, not live graph handles. Existing node,
// manifest and block limits remain independent. Count every reconstructed
// identity occurrence, even when many rows refer to the same tiny text atom.
export const MAX_RUNTIME_IDENTITY_UNITS = 384 * 1024 * 1024;
export const MAX_RUNTIME_EDGES = 2_000_000;
export const MAX_RUNTIME_BINDINGS = 2_000_000;
// Overflow lives in the existing 64 MiB manifest. Bound retained tuples before
// serializing the complete manifest, whose independent limit still applies.
const MAX_RUNTIME_OVERFLOW_BYTES = 64 * 1024 * 1024;
const MAX_ROWS = 512;
const MAX_ATOM_UNITS = 128 * 1024;
const EDGE_MAGIC = 0x33474445;
const MAP_MAGIC = 0x3350414d;
const EDGE_BYTES = 40;
const MAP_BYTES = 16;
const HEADER_BYTES = 8;
const GENERATED_ID =
  /^(r:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}:(?:e:)?)([1-9][0-9]{0,9})$/;
const SIDES = ['top', 'right', 'bottom', 'left'] as const;
const META = 1,
  META_UNDEFINED = 2;
const META_FIELDS = ['sourceTileId', 'targetTileId', 'targetSide'] as const;
type EdgeRow = [
  string,
  string,
  string,
  number,
  string | null,
  string | null,
  string | null,
];
type MapRow = [string, string];
type Segment<T> = { root: number; count: number } | { overflow: T[] };
export type PackedRuntimeGraph = {
  edges: Segment<EdgeRow>[];
  bindings?: Segment<MapRow>[];
};
type Instance = ProjectSnapshotData['chips'][number]['editingInstanceDto'];

class IdentityBudget {
  private maximumEdges: number;
  private maximumBindings: number;
  private maximumUnits: number;
  constructor(limits?: ProjectDecodeLimits) {
    this.maximumEdges = lowerDecodeLimit(limits?.runtimeEdges, MAX_RUNTIME_EDGES);
    this.maximumBindings = lowerDecodeLimit(
      limits?.runtimeBindings,
      MAX_RUNTIME_BINDINGS,
    );
    this.maximumUnits = lowerDecodeLimit(
      limits?.runtimeIdentityUnits,
      MAX_RUNTIME_IDENTITY_UNITS,
    );
  }
  units = 0;
  edges = 0;
  bindings = 0;
  rows(count: number, edge: boolean): void {
    if (edge) this.edges += count;
    else this.bindings += count;
    if (this.edges > this.maximumEdges || this.bindings > this.maximumBindings)
      throw new RangeError('Current runtime record count exceeds limit');
  }
  add(units: number): void {
    this.units += units;
    if (!Number.isSafeInteger(units) || units < 0 || this.units > this.maximumUnits)
      throw new RangeError('Current runtime identity expansion exceeds limit');
  }
}

class OverflowBudget {
  #bytes = 0;
  constructor(private readonly limit: number) {
    if (!Number.isSafeInteger(limit) || limit < 0 || limit > MAX_RUNTIME_OVERFLOW_BYTES)
      throw new TypeError('Invalid runtime overflow budget');
  }
  #add(bytes: number): void {
    this.#bytes += bytes;
    if (this.#bytes > this.limit)
      throw new RangeError('Current runtime overflow exceeds manifest budget');
  }
  #string(value: string): void {
    // Every UTF-16 unit needs at least one JSON byte. Reject before scanning a
    // large opaque identity; count without allocating escaped strings or a pool.
    if (value.length > this.limit - this.#bytes)
      throw new RangeError('Current runtime overflow exceeds manifest budget');
    this.#add(2);
    for (let i = 0; i < value.length; i++) {
      const code = value.charCodeAt(i);
      if (code === 0x22 || code === 0x5c) this.#add(2);
      else if (code < 0x20)
        this.#add(
          code === 8 || code === 9 || code === 10 || code === 12 || code === 13 ? 2 : 6,
        );
      else if (code < 0x80) this.#add(1);
      else if (code < 0x800) this.#add(2);
      else if (code >= 0xd800 && code <= 0xdbff) {
        const next = value.charCodeAt(i + 1);
        if (next >= 0xdc00 && next <= 0xdfff) {
          this.#add(4);
          i++;
        } else this.#add(6);
      } else this.#add(code >= 0xdc00 && code <= 0xdfff ? 6 : 3);
    }
  }
  row(row: EdgeRow | MapRow, first: boolean): void {
    // Include {"overflow":[...]} and separators, as written in the manifest.
    this.#add((first ? 15 : 1) + 2 + row.length - 1);
    for (const value of row) {
      if (typeof value === 'string') this.#string(value);
      else this.#add(value === null ? 4 : String(value).length);
    }
  }
}

function edgeRow(input: LogicEdgeDto): EdgeRow {
  const edge = LogicEdgeDtoSchema.parse(input);
  let flags = Object.hasOwn(edge, 'meta') ? META : 0;
  const values: Array<string | null> = [null, null, null];
  if (flags && edge.meta === undefined) flags |= META_UNDEFINED;
  if (edge.meta)
    META_FIELDS.forEach((key, index) => {
      if (!Object.hasOwn(edge.meta!, key)) return;
      flags |= 1 << (2 + index * 2);
      if (edge.meta![key] === undefined) flags |= 1 << (3 + index * 2);
      else values[index] = edge.meta![key]!;
    });
  return [edge.id, edge.sourceNodeId, edge.targetNodeId, flags, ...values] as EdgeRow;
}

function validateEdgeRow(value: unknown): asserts value is EdgeRow {
  if (
    !Array.isArray(value) ||
    value.length !== 7 ||
    !value.slice(0, 3).every((x) => typeof x === 'string') ||
    !Number.isInteger(value[3]) ||
    value[3] < 0 ||
    value[3] > 255
  )
    throw new TypeError('Invalid packed edge record');
  const flags = value[3];
  if ((!(flags & META) && flags !== 0) || (flags & META_UNDEFINED && flags !== 3))
    throw new TypeError('Invalid packed edge metadata flags');
  for (let i = 0; i < 3; i++) {
    const present = flags & (1 << (2 + i * 2));
    const missingValue = flags & (1 << (3 + i * 2));
    if (missingValue && !present) throw new TypeError('Invalid packed optional metadata');
    const field = value[4 + i];
    if (present && !missingValue) {
      if (
        typeof field !== 'string' ||
        (i === 2 && !(SIDES as readonly string[]).includes(field))
      )
        throw new TypeError('Invalid packed edge metadata value');
    } else if (field !== null)
      throw new TypeError('Unexpected packed edge metadata value');
  }
}

function restoreEdge(row: EdgeRow): LogicEdgeDto {
  const edge: LogicEdgeDto = { id: row[0], sourceNodeId: row[1], targetNodeId: row[2] };
  const flags = row[3];
  if (flags & META) {
    edge.meta = undefined;
    if (!(flags & META_UNDEFINED)) {
      edge.meta = {};
      META_FIELDS.forEach((key, i) => {
        if (flags & (1 << (2 + i * 2)))
          Object.defineProperty(edge.meta!, key, {
            value: flags & (1 << (3 + i * 2)) ? undefined : row[4 + i],
            enumerable: true,
            writable: true,
            configurable: true,
          });
      });
    }
  }
  return edge;
}

function identity(value: string): readonly [string, number] {
  const match = GENERATED_ID.exec(value);
  if (match) {
    const suffix = Number(match[2]);
    if (suffix <= 0xffff_ffff && match[1] + suffix === value) return [match[1], suffix];
  }
  return [value, 0];
}

function chargeRow(row: EdgeRow | MapRow, budget: IdentityBudget): void {
  for (const value of row) if (typeof value === 'string') budget.add(value.length);
}

function compactable(row: EdgeRow | MapRow): boolean {
  return row.every(
    (value) => typeof value !== 'string' || value.length <= MAX_ATOM_UNITS,
  );
}

function rowsBlock(
  rows: Array<EdgeRow | MapRow>,
  edge: boolean,
  builder: SnapshotBlockBuilder,
): SnapshotBlock {
  const width = edge ? EDGE_BYTES : MAP_BYTES;
  const bytes = new ArrayBuffer(HEADER_BYTES + rows.length * width);
  const view = new DataView(bytes);
  view.setUint32(0, edge ? EDGE_MAGIC : MAP_MAGIC, true);
  view.setUint32(4, rows.length, true);
  const atoms: string[] = [],
    indexes = new Map<string, number>();
  const intern = (value: string) => {
    let index = indexes.get(value);
    if (index === undefined) {
      index = atoms.length;
      atoms.push(value);
      indexes.set(value, index);
    }
    return index;
  };
  const putIdentity = (offset: number, value: string) => {
    const [text, suffix] = identity(value);
    view.setUint32(offset, intern(text), true);
    view.setUint32(offset + 4, suffix, true);
  };
  rows.forEach((value, index) => {
    const offset = HEADER_BYTES + index * width;
    if (edge) {
      const row = value as EdgeRow;
      view.setUint32(offset, row[3], true);
      for (let i = 0; i < 3; i++) putIdentity(offset + 4 + i * 8, row[i] as string);
      for (let i = 0; i < 2; i++)
        if (row[4 + i] !== null)
          view.setUint32(offset + 28 + i * 4, intern(row[4 + i] as string), true);
      if (row[6] !== null)
        view.setUint32(
          offset + 36,
          SIDES.indexOf(row[6] as (typeof SIDES)[number]) + 1,
          true,
        );
    } else {
      const row = value as MapRow;
      putIdentity(offset, row[0]);
      putIdentity(offset + 8, row[1]);
    }
  });
  return builder.addRoot(bytes, atoms);
}

function* instances(project: ProjectSnapshotData): Iterable<Instance> {
  for (const chip of project.chips)
    yield* [...chip.chipInstanceDtos, chip.editingInstanceDto];
}

/** Pack metadata only; the snapshot's owned node columns remain untouched. */
export async function packRuntimeRecords(
  project: ProjectSnapshotData,
  limits: {
    readonly blocks?: Partial<SnapshotBudgets>;
    readonly overflowBytes?: number;
  } = {},
): Promise<{
  project: ProjectSnapshotData;
  graphs: PackedRuntimeGraph[];
  roots: readonly SnapshotBlock[];
  identityUnits: number;
}> {
  const builder = new SnapshotBlockBuilder(limits.blocks),
    budget = new IdentityBudget(),
    overflowBudget = new OverflowBudget(
      limits.overflowBytes ?? MAX_RUNTIME_OVERFLOW_BYTES,
    );
  const graphs: PackedRuntimeGraph[] = [],
    roots: SnapshotBlock[] = [];
  let packingDisabled = false,
    started = performance.now();
  const pause = async () => {
    if (performance.now() - started >= 8) {
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      started = performance.now();
    }
  };
  async function pack<T extends EdgeRow | MapRow>(
    source: Iterable<T>,
    edge: boolean,
  ): Promise<Segment<T>[]> {
    const result: Segment<T>[] = [];
    let compact: T[] = [],
      overflow: T[] = [];
    const appendOverflow = (row: T) => {
      overflowBudget.row(row, overflow.length === 0);
      overflow.push(row);
    };
    const flush = () => {
      if (compact.length) {
        try {
          const root = rowsBlock(compact, edge, builder);
          result.push({ root: roots.length, count: compact.length });
          roots.push(root);
        } catch (error) {
          if (!(error instanceof SnapshotLimit)) throw error;
          // Successful immutable roots remain owned by roots. Release the pool,
          // including atoms from the failed batch, and never retry this builder.
          builder.abandon();
          packingDisabled = true;
          for (const row of compact) appendOverflow(row);
        }
        compact = [];
      }
      if (overflow.length) {
        result.push({ overflow });
        overflow = [];
      }
    };
    for (const row of source) {
      budget.rows(1, edge);
      chargeRow(row, budget);
      if (!packingDisabled && compactable(row)) {
        if (overflow.length) flush();
        compact.push(row);
      } else {
        if (compact.length) flush();
        appendOverflow(row);
      }
      if (compact.length === MAX_ROWS || overflow.length === MAX_ROWS) {
        flush();
        await pause();
      }
    }
    flush();
    await pause();
    return result;
  }
  const copyInstance = async (value: Instance): Promise<Instance> => {
    const graph: PackedRuntimeGraph = {
      edges: await pack(
        (function* () {
          for (const edge of value.logicGraphDto.edges) yield edgeRow(edge);
        })(),
        true,
      ),
    };
    if (value.templateToInstanceIdMap !== undefined) {
      const map = value.templateToInstanceIdMap;
      if (!map || typeof map !== 'object' || Array.isArray(map))
        throw new TypeError('Invalid runtime bindings');
      graph.bindings = await pack(
        (function* () {
          for (const key of Object.keys(map)) {
            const target = map[key];
            if (typeof target !== 'string')
              throw new TypeError('Invalid runtime binding identity');
            yield [key, target] as MapRow;
          }
        })(),
        false,
      );
    }
    graphs.push(graph);
    return {
      ...value,
      ...(graph.bindings ? { templateToInstanceIdMap: {} } : {}),
      logicGraphDto: { ...value.logicGraphDto, edges: [] },
    };
  };
  try {
    const chips: ProjectSnapshotData['chips'] = [];
    for (const chip of project.chips) {
      const chipInstanceDtos: Instance[] = [];
      for (const value of chip.chipInstanceDtos)
        chipInstanceDtos.push(await copyInstance(value));
      chips.push({
        ...chip,
        chipInstanceDtos,
        editingInstanceDto: await copyInstance(chip.editingInstanceDto),
      });
    }
    if (!packingDisabled) builder.finish();
    return { project: { ...project, chips }, graphs, roots, identityUnits: budget.units };
  } catch (error) {
    builder.abandon();
    throw error;
  }
}

function dense(value: unknown): asserts value is unknown[] {
  if (
    !Array.isArray(value) ||
    Object.keys(value).length !== value.length ||
    !value.every((_, i) => Object.hasOwn(value, i))
  )
    throw new TypeError('Invalid packed runtime array');
}

/** Reject ambiguous graph/root ownership before reading any block payload. */
export function validatePackedRuntimeLayout(
  project: ProjectSnapshotData,
  input: unknown,
  rootCount: number,
  onRows?: (total: number) => void,
  limits?: ProjectDecodeLimits,
): asserts input is PackedRuntimeGraph[] {
  dense(input);
  const owners = [...instances(project)];
  if (input.length !== owners.length)
    throw new TypeError('Packed runtime graph count mismatch');
  const used = new Set<number>();
  const budget = new IdentityBudget(limits);
  let totalRows = 0;
  const segments = (value: unknown, edge: boolean) => {
    dense(value);
    for (const segment of value) {
      if (!segment || typeof segment !== 'object' || Array.isArray(segment))
        throw new TypeError('Invalid packed runtime segment');
      if ('overflow' in segment) {
        if (Object.keys(segment).length !== 1)
          throw new TypeError('Invalid packed overflow fields');
        dense(segment.overflow);
        if (!segment.overflow.length || segment.overflow.length > MAX_ROWS)
          throw new TypeError('Invalid packed overflow count');
        budget.rows(segment.overflow.length, edge);
        totalRows += segment.overflow.length;
        for (const row of segment.overflow) {
          if (edge) validateEdgeRow(row);
          else if (
            !Array.isArray(row) ||
            row.length !== 2 ||
            !row.every((x) => typeof x === 'string')
          )
            throw new TypeError('Invalid packed binding record');
        }
      } else {
        const row = segment as { root: number; count: number };
        if (
          Object.keys(row).length !== 2 ||
          !Number.isSafeInteger(row.root) ||
          row.root < 0 ||
          row.root >= rootCount ||
          !Number.isSafeInteger(row.count) ||
          row.count < 1 ||
          row.count > MAX_ROWS ||
          used.has(row.root)
        )
          throw new TypeError('Invalid or shared packed runtime root');
        used.add(row.root);
        budget.rows(row.count, edge);
        totalRows += row.count;
      }
    }
  };
  for (let i = 0; i < input.length; i++) {
    const graph = input[i] as PackedRuntimeGraph;
    const owner = owners[i];
    if (
      !graph ||
      typeof graph !== 'object' ||
      Array.isArray(graph) ||
      Object.keys(graph).some((key) => key !== 'edges' && key !== 'bindings') ||
      owner.logicGraphDto.edges.length
    )
      throw new TypeError('Invalid packed runtime owner');
    segments(graph.edges, true);
    if (graph.bindings !== undefined) {
      if (
        !owner.templateToInstanceIdMap ||
        Object.keys(owner.templateToInstanceIdMap).length
      )
        throw new TypeError('Invalid packed binding placeholder');
      segments(graph.bindings, false);
    } else if (owner.templateToInstanceIdMap !== undefined)
      throw new TypeError('Missing packed bindings');
  }
  if (used.size !== rootCount) throw new TypeError('Unused packed runtime root');
  onRows?.(totalRows);
}

/** Reconstruct validated schema values under an explicit expansion bound. */
export async function unpackRuntimeRecords(
  project: ProjectSnapshotData,
  graphs: PackedRuntimeGraph[],
  roots: readonly SnapshotBlock[],
  onProgress?: import('@/utils/projectLoadProgress').ProjectLoadWorkObserver,
  signal?: AbortSignal,
  limits?: ProjectDecodeLimits,
): Promise<ProjectSnapshotData> {
  let totalRows = 0;
  validatePackedRuntimeLayout(
    project,
    graphs,
    roots.length,
    (total) => {
      totalRows = total;
    },
    limits,
  );
  let completedRows = 0;
  if (new Set(roots).size !== roots.length)
    throw new TypeError('Shared packed runtime root');
  const budget = new IdentityBudget(limits),
    texts = new WeakMap<SnapshotBlock, string>();
  let started = performance.now();
  const pause = async () => {
    signal?.throwIfAborted();
    if (performance.now() - started >= 8) {
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      started = performance.now();
      signal?.throwIfAborted();
    }
  };
  const readBlock = (
    segment: { root: number; count: number },
    edge: boolean,
  ): Array<EdgeRow | MapRow> => {
    const block = roots[segment.root],
      width = edge ? EDGE_BYTES : MAP_BYTES;
    if (
      block.byteLength !== HEADER_BYTES + segment.count * width ||
      block.readUint32(0) !== (edge ? EDGE_MAGIC : MAP_MAGIC) ||
      block.readUint32(4) !== segment.count ||
      new Set(block.dependencies).size !== block.dependencies.length
    )
      throw new TypeError('Invalid packed runtime block');
    const used = new Set<number>();
    const text = (index: number, suffix = 0): string => {
      if (index >= block.dependencies.length)
        throw new TypeError('Invalid packed identity index');
      const atom = block.dependencies[index];
      if (atom.dependencies.length || atom.byteLength % 2)
        throw new TypeError('Invalid packed identity atom');
      used.add(index);
      budget.add(atom.byteLength / 2 + (suffix ? String(suffix).length : 0));
      let value = texts.get(atom);
      if (value === undefined) {
        value = atom.readText();
        texts.set(atom, value);
      }
      if (!suffix) return value;
      const result = value + suffix;
      const parts = identity(result);
      if (parts[0] !== value || parts[1] !== suffix)
        throw new TypeError('Invalid generated runtime identity');
      return result;
    };
    const rows: Array<EdgeRow | MapRow> = [];
    for (let i = 0; i < segment.count; i++) {
      const offset = HEADER_BYTES + i * width;
      const readIdentity = (at: number) =>
        text(block.readUint32(at), block.readUint32(at + 4));
      if (!edge) {
        rows.push([readIdentity(offset), readIdentity(offset + 8)]);
        continue;
      }
      const flags = block.readUint32(offset),
        values: Array<string | null> = [null, null, null];
      for (let field = 0; field < 3; field++) {
        const word = block.readUint32(offset + 28 + field * 4);
        if (flags & (1 << (2 + field * 2)) && !(flags & (1 << (3 + field * 2)))) {
          if (field < 2) values[field] = text(word);
          else {
            if (!word || word > SIDES.length)
              throw new TypeError('Invalid packed edge side');
            values[field] = SIDES[word - 1];
            budget.add(values[field]!.length);
          }
        } else if (word) throw new TypeError('Unexpected packed optional column');
      }
      const row = [
        readIdentity(offset + 4),
        readIdentity(offset + 12),
        readIdentity(offset + 20),
        flags,
        ...values,
      ] as EdgeRow;
      validateEdgeRow(row);
      rows.push(row);
    }
    if (used.size !== block.dependencies.length)
      throw new TypeError('Unused packed identity atom');
    return rows;
  };
  async function read<T extends EdgeRow | MapRow>(
    segments: Segment<T>[],
    edge: boolean,
    accept: (row: T) => void,
  ) {
    for (const segment of segments) {
      signal?.throwIfAborted();
      if ('overflow' in segment) {
        for (const row of segment.overflow) {
          chargeRow(row, budget);
          accept(row);
        }
      } else for (const row of readBlock(segment, edge)) accept(row as T);
      completedRows += 'overflow' in segment ? segment.overflow.length : segment.count;
      // The final unit is synchronous snapshot/DTO materialization, reported by
      // the caller after it returns. Count rows in validation's existing pass.
      onProgress?.(completedRows, totalRows + 1);
      await pause();
    }
  }
  let index = 0;
  const copyInstance = async (value: Instance): Promise<Instance> => {
    const graph = graphs[index++],
      edges: LogicEdgeDto[] = [];
    await read(graph.edges, true, (row) => edges.push(restoreEdge(row)));
    let map: Record<string, string> | undefined;
    if (graph.bindings !== undefined) {
      map = {};
      await read(graph.bindings, false, ([key, target]) => {
        if (Object.hasOwn(map!, key)) throw new TypeError('Duplicate packed binding key');
        Object.defineProperty(map!, key, {
          value: target,
          enumerable: true,
          writable: true,
          configurable: true,
        });
      });
    }
    return {
      ...value,
      ...(map ? { templateToInstanceIdMap: map } : {}),
      logicGraphDto: { ...value.logicGraphDto, edges },
    };
  };
  const chips: ProjectSnapshotData['chips'] = [];
  for (const chip of project.chips) {
    const chipInstanceDtos: Instance[] = [];
    for (const value of chip.chipInstanceDtos)
      chipInstanceDtos.push(await copyInstance(value));
    chips.push({
      ...chip,
      chipInstanceDtos,
      editingInstanceDto: await copyInstance(chip.editingInstanceDto),
    });
  }
  return { ...project, chips };
}
